// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * game.ts — the pure model. The decision IS the game, so this file is the game.
 *
 * A race is a sequence of pit stops. Between stops a stint runs. Four systems —
 * tyres, fuel, wing, brakes — each carry a hidden "severity" in [0,1] (0 = fresh,
 * 1 = critical). At each stop you SEE noisy, sometimes-offline telemetry and a
 * subjective radio line, and you CHOOSE which systems the crew services. The cost
 * model is the whole design:
 *
 *   - Doing a job costs `jobTime` seconds NOW (a longer stop). It resets that
 *     system near zero.
 *   - Leaving a system costs `penalty(sev)` seconds over the coming stint (a car
 *     carrying a fault is slow), and the fault ESCALATES into the next stop.
 *   - Sending a system out at or past its catastrophe threshold blows up
 *     (puncture / brake failure / running dry) — a big fixed penalty.
 *
 * Total race time = Σ stop times + Σ stint penalties + Σ catastrophes. Lowest
 * wins. A full service is slow; the bare minimum blows up; the answer is triage.
 *
 * DETERMINISM. Every hidden dynamic — the wear each stint, the incident spikes,
 * the telemetry noise, which sensor drops out, whether the driver tells the truth
 * — is drawn from the seed, ONCE, up front, independent of any choice. The
 * *state* then follows from your calls. So two peers on the same seed face an
 * identical environment; if they make the same calls they see the same car, and
 * if they differ they each live with their own decisions. Nothing about one
 * peer's run touches another's — which is what makes the versus race
 * structurally desync-proof (see race.ts).
 *
 * The cost model decomposes PER SYSTEM (stop time is additive per job; penalties
 * are per system; escalation couples a system only to itself), so the
 * retrospectively-optimal line is an exact per-system search over the fixed wear
 * schedule — `optimalLine()`. That is both the balance baseline and the "here is
 * what you left on the table" on the results screen.
 */

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';

export type System = 'tyres' | 'fuel' | 'wing' | 'brakes';
export const SYSTEMS: readonly System[] = ['tyres', 'fuel', 'wing', 'brakes'];

export const SYSTEM_NAME: Record<System, string> = {
  tyres: 'Tyres',
  fuel: 'Fuel',
  wing: 'Front wing',
  brakes: 'Brakes',
};

// ── tuning (the balance sim referees these) ─────────────────────────────────

/** Seconds added to the stop for servicing a system. */
export const JOB_TIME: Record<System, number> = { tyres: 2.6, fuel: 3.2, wing: 1.8, brakes: 1.4 };
/** Base seconds for the stop itself (enter box, stop, launch), before any job. */
export const BASE_STOP = 1.4;
/** Severity a system is left at after servicing it. */
const RESET: Record<System, number> = { tyres: 0.02, fuel: 0.03, wing: 0.0, brakes: 0.06 };
/** The reset values, exposed for the cost-model unit test. */
export const RESET_FOR_TEST: Readonly<Record<System, number>> = RESET;
/** Base severity growth per stint. */
const WEAR: Record<System, number> = { tyres: 0.26, fuel: 0.42, wing: 0.06, brakes: 0.14 };
/** Random jitter (±) on each stint's wear. */
const WEAR_JITTER: Record<System, number> = { tyres: 0.06, fuel: 0.03, wing: 0.04, brakes: 0.06 };
/** Penalty (s) over a stint for carrying a system at severity `sev`: K·sev². */
export const PENALTY_K: Record<System, number> = { tyres: 7, fuel: 9, wing: 6, brakes: 5 };
/** Send a system out at/above this severity and it blows up during the stint. */
export const CATA_THRESH: Record<System, number> = {
  tyres: 0.86,
  fuel: 0.9,
  wing: 0.88,
  brakes: 0.86,
};
/** The catastrophe penalty (s). Fuel is nearly race-ending. */
export const CATA_COST: Record<System, number> = { tyres: 22, fuel: 55, wing: 26, brakes: 30 };
/** Incident spike added to a stint's wear when an incident fires (wing/brakes). */
const INCIDENT_SPIKE: Record<System, number> = { tyres: 0, fuel: 0, wing: 0.5, brakes: 0.38 };
/** Per-system multiplier on telemetry noise (fuel gauge is reliable). */
const NOISE_MULT: Record<System, number> = { tyres: 1.0, fuel: 0.35, wing: 1.25, brakes: 1.15 };
/** Per-system multiplier on the sensor-offline chance. */
const OFFLINE_MULT: Record<System, number> = { tyres: 0.9, fuel: 0.15, wing: 1.6, brakes: 1.4 };
/** Starting severity when the first car comes in (it has been running a while). */
const INIT: Record<System, number> = { tyres: 0.34, fuel: 0.44, wing: 0.06, brakes: 0.22 };
const INIT_JITTER: Record<System, number> = { tyres: 0.08, fuel: 0.06, wing: 0.05, brakes: 0.08 };

/** Penalty seconds for carrying `sev` on `system` over one stint (excl. catastrophe). */
export function penaltyFor(system: System, sev: number): number {
  return PENALTY_K[system] * sev * sev;
}

/** Does sending `system` out at `sev` trigger a catastrophe this stint? */
export function isCatastrophe(system: System, sev: number): boolean {
  return sev >= CATA_THRESH[system];
}

/** The nominal (perfect-execution) stop time for a fix set. */
export function nominalStopTime(fix: ReadonlySet<System>): number {
  let t = BASE_STOP;
  for (const s of SYSTEMS) if (fix.has(s)) t += JOB_TIME[s];
  return t;
}

// ── the seed-drawn environment ───────────────────────────────────────────────

/** Everything the seed fixes about one stint, independent of any choice. */
interface StintEnv {
  /** Extra severity every system gains this stint (incl. incident spikes). */
  wearInc: Record<System, number>;
}

/** What the seed fixes about the information available at one stop. */
interface StopEnv {
  /** Telemetry offset added to the true severity (the reading may lie a little). */
  noise: Record<System, number>;
  /** This system's sensor is dark at this stop — no reading, only the radio. */
  offline: Record<System, boolean>;
  /** The driver tells the truth about the worst system this stop. */
  radioHonest: boolean;
  /** The driver says nothing this stop. */
  radioSilent: boolean;
  /** If dishonest and not silent, the (wrong) system the driver names. */
  radioWrong: System;
  /** A stable index into the phrase bank so the same stop reads the same line. */
  phrase: number;
}

export interface RaceEnv {
  init: Record<System, number>;
  stints: StintEnv[];
  stops: StopEnv[];
}

function draws(rng: Rng, mode: Mode): RaceEnv {
  const init = {} as Record<System, number>;
  for (const s of SYSTEMS) {
    init[s] = clamp01(INIT[s] + (rng() * 2 - 1) * INIT_JITTER[s]);
  }

  const stints: StintEnv[] = [];
  const stops: StopEnv[] = [];
  for (let k = 0; k < mode.stops; k++) {
    const wearInc = {} as Record<System, number>;
    for (const s of SYSTEMS) {
      let w = (WEAR[s] + (rng() * 2 - 1) * WEAR_JITTER[s]) * mode.wearScale;
      if (INCIDENT_SPIKE[s] > 0 && rng() < mode.incident) w += INCIDENT_SPIKE[s];
      wearInc[s] = Math.max(0, w);
    }
    stints.push({ wearInc });

    const noise = {} as Record<System, number>;
    const offline = {} as Record<System, boolean>;
    for (const s of SYSTEMS) {
      // A symmetric triangular-ish noise so the reading brackets the truth.
      noise[s] = (rng() + rng() - 1) * mode.teleNoise * NOISE_MULT[s];
      offline[s] = rng() < mode.teleOffline * OFFLINE_MULT[s];
    }
    stops.push({
      noise,
      offline,
      radioHonest: rng() < mode.radioHonest,
      radioSilent: rng() < mode.radioSilent,
      radioWrong: SYSTEMS[Math.floor(rng() * SYSTEMS.length)],
      phrase: Math.floor(rng() * 1000),
    });
  }

  return { init, stints, stops };
}

// ── radio phrasebank ─────────────────────────────────────────────────────────

const RADIO_LINES: Record<System, readonly string[]> = {
  tyres: ['No grip out here.', 'The rears are gone.', "I'm sliding everywhere.", 'Tyres are cooked.'],
  fuel: ["Might be tight on fuel.", 'Keep an eye on the fuel.', "I'm lifting and coasting already."],
  wing: ["Front's gone.", 'Lost the front end.', "Something's flapping out here.", 'I brushed the wall.'],
  brakes: ['No brakes into one.', 'The pedal is long.', "Can't slow the thing down."],
};
const RADIO_FINE: readonly string[] = ['Car feels good.', 'Happy with the balance.', 'All good back here.'];

export interface RadioLine {
  /** The system the driver is pointing at, or null for "feels fine" / silence. */
  system: System | null;
  text: string;
  /** True when the driver is raising an alarm (drives the SFX + emphasis). */
  alarm: boolean;
}

// ── what a stop looks like to the player ─────────────────────────────────────

export interface Telemetry {
  system: System;
  /** 0..1 reading, or null when the sensor is offline. */
  reading: number | null;
}

export interface StopView {
  stopIndex: number; // 0-based
  stops: number;
  telemetry: Telemetry[];
  radio: RadioLine;
}

// ── the per-stint outcome the UI narrates ────────────────────────────────────

export interface SystemOutcome {
  system: System;
  fixed: boolean;
  /** Severity the car went back out on. */
  sevOut: number;
  penalty: number;
  catastrophe: boolean;
}

export interface StintOutcome {
  stopIndex: number;
  stopTime: number;
  execPenalty: number;
  systems: SystemOutcome[];
  /** stopTime + Σ penalties + Σ catastrophes for this stop+stint. */
  lost: number;
}

// ── results ──────────────────────────────────────────────────────────────────

export interface BreakdownRow {
  stopIndex: number;
  fixed: System[];
  stopTime: number;
  penalty: number;
  catastrophes: System[];
  /** The perfect fix set for this stop on this seed. */
  optimalFix: System[];
}

export interface Summary {
  /** Total race time in seconds (lower is better). */
  total: number;
  /** The optimal achievable total on this seed (perfect calls, perfect stops). */
  optimal: number;
  /** total − optimal: seconds left on the table. */
  lost: number;
  stops: number;
  catastrophes: number;
  rows: BreakdownRow[];
}

// ── the optimal line (exact, per-system) ─────────────────────────────────────

/** The cost a single system contributes under one fix/skip sequence. */
function systemCost(system: System, env: RaceEnv, fixSeq: boolean[]): number {
  let sev = env.init[system];
  let cost = 0;
  for (let k = 0; k < fixSeq.length; k++) {
    const sevOut = fixSeq[k] ? RESET[system] : sev;
    if (fixSeq[k]) cost += JOB_TIME[system];
    cost += penaltyFor(system, sevOut);
    if (isCatastrophe(system, sevOut)) cost += CATA_COST[system];
    sev = clamp01(sevOut + env.stints[k].wearInc[system]);
  }
  return cost;
}

/** Exhaustive best fix/skip sequence for one system over the whole race. */
function bestSequence(system: System, env: RaceEnv): { seq: boolean[]; cost: number } {
  const n = env.stops.length;
  let best: boolean[] = new Array(n).fill(false);
  let bestCost = Infinity;
  for (let mask = 0; mask < 1 << n; mask++) {
    const seq: boolean[] = [];
    for (let k = 0; k < n; k++) seq.push((mask & (1 << k)) !== 0);
    const c = systemCost(system, env, seq);
    if (c < bestCost) {
      bestCost = c;
      best = seq;
    }
  }
  return { seq: best, cost: bestCost };
}

export interface OptimalLine {
  /** Per stop, the set of systems the perfect line services. */
  fixByStop: Set<System>[];
  /** The perfect total (BASE_STOP per stop + the per-system optima). */
  total: number;
}

export function optimalLine(mode: Mode, seed: number): OptimalLine {
  const env = draws(makeRng(seed >>> 0), mode);
  return optimalFromEnv(env);
}

function optimalFromEnv(env: RaceEnv): OptimalLine {
  const n = env.stops.length;
  const fixByStop: Set<System>[] = Array.from({ length: n }, () => new Set<System>());
  let total = BASE_STOP * n;
  for (const s of SYSTEMS) {
    const { seq, cost } = bestSequence(s, env);
    total += cost;
    for (let k = 0; k < n; k++) if (seq[k]) fixByStop[k].add(s);
  }
  return { fixByStop, total };
}

// ── the live run ─────────────────────────────────────────────────────────────

export interface GameOpts {
  seed: number;
  mode: Mode;
}

export class Game {
  readonly seed: number;
  readonly mode: Mode;
  readonly env: RaceEnv;

  private idx = 0;
  private sev: Record<System, number>;
  private rows: BreakdownRow[] = [];
  private cum = 0;
  private readonly opt: OptimalLine;

  constructor(opts: GameOpts) {
    this.seed = opts.seed >>> 0;
    this.mode = opts.mode;
    this.env = draws(makeRng(this.seed), opts.mode);
    this.sev = { ...this.env.init };
    this.opt = optimalFromEnv(this.env);
  }

  get stopIndex(): number {
    return this.idx;
  }
  get stops(): number {
    return this.mode.stops;
  }
  get over(): boolean {
    return this.idx >= this.mode.stops;
  }
  /** Time banked so far (seconds). */
  liveTotal(): number {
    return this.cum;
  }

  /** The true severity of a system right now (hidden from the player; for tests). */
  trueSev(system: System): number {
    return this.sev[system];
  }

  /** What the pit wall sees for the current stop. */
  view(): StopView {
    const env = this.env.stops[this.idx];
    const telemetry: Telemetry[] = SYSTEMS.map((s) => ({
      system: s,
      reading: env.offline[s] ? null : clamp01(this.sev[s] + env.noise[s]),
    }));
    return { stopIndex: this.idx, stops: this.mode.stops, telemetry, radio: this.radio() };
  }

  /** The driver's (subjective, sometimes wrong) radio line for this stop. */
  private radio(): RadioLine {
    const env = this.env.stops[this.idx];
    if (env.radioSilent) {
      return { system: null, text: pickPhrase(RADIO_FINE, env.phrase), alarm: false };
    }
    // The true worst system — what an honest driver would flag.
    let worst: System = 'tyres';
    for (const s of SYSTEMS) if (this.sev[s] > this.sev[worst]) worst = s;
    // Only worth mentioning if it is actually a problem; otherwise "feels fine".
    if (this.sev[worst] < 0.3 && env.radioHonest) {
      return { system: null, text: pickPhrase(RADIO_FINE, env.phrase), alarm: false };
    }
    const target = env.radioHonest ? worst : env.radioWrong;
    return {
      system: target,
      text: pickPhrase(RADIO_LINES[target], env.phrase),
      alarm: true,
    };
  }

  /**
   * Commit the crew's calls for this stop.
   * @param fix the systems to service
   * @param execPenalty seconds added by fumbles + unsafe releases in execution
   */
  commit(fix: ReadonlySet<System>, execPenalty = 0): StintOutcome {
    if (this.over) throw new Error('the race is already over');
    const stint = this.env.stints[this.idx];
    const stopTime = nominalStopTime(fix) + Math.max(0, execPenalty);

    const systems: SystemOutcome[] = [];
    let penaltySum = 0;
    const cataList: System[] = [];
    for (const s of SYSTEMS) {
      const fixed = fix.has(s);
      const sevOut = fixed ? RESET[s] : this.sev[s];
      const penalty = penaltyFor(s, sevOut);
      const cata = isCatastrophe(s, sevOut);
      penaltySum += penalty;
      if (cata) cataList.push(s);
      systems.push({ system: s, fixed, sevOut, penalty, catastrophe: cata });
      // Escalate into the next stop.
      this.sev[s] = clamp01(sevOut + stint.wearInc[s]);
    }

    const cataCost = cataList.reduce((n, s) => n + CATA_COST[s], 0);
    const lost = stopTime + penaltySum + cataCost;
    this.cum += lost;

    this.rows.push({
      stopIndex: this.idx,
      fixed: SYSTEMS.filter((s) => fix.has(s)),
      stopTime,
      penalty: penaltySum + cataCost,
      catastrophes: cataList,
      optimalFix: [...this.opt.fixByStop[this.idx]],
    });

    this.idx++;
    return { stopIndex: this.idx - 1, stopTime, execPenalty, systems, lost };
  }

  summary(): Summary {
    const catastrophes = this.rows.reduce((n, r) => n + r.catastrophes.length, 0);
    return {
      total: this.cum,
      optimal: this.opt.total,
      lost: Math.max(0, this.cum - this.opt.total),
      stops: this.mode.stops,
      catastrophes,
      rows: this.rows,
    };
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function pickPhrase(bank: readonly string[], n: number): string {
  return bank[n % bank.length];
}

/** A UTC daily seed, so "today's race" is the same for everyone. */
export function dailySeed(): number {
  const now = new Date();
  const key = `${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
